/*
 * Pure helpers for the admin race tracker calendar. Day strings are
 * YYYY-MM-DD in local time. The month grid is built by hand: preact-util's
 * getWeeksInMonth drops 1 January when it falls in ISO week 52.
 */

const pad2 = n => String(n).padStart(2, '0');

export function dayString(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function addDays(day, n) {
    const [y, m, d] = String(day).split('-').map(part => parseInt(part, 10));
    return dayString(new Date(y, m - 1, d + n));
}

// Monday-first weeks covering the whole month.
export function monthGrid(year, month) {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    // getDay(): 0 = Sunday. Shift so Monday = 0.
    const leadDays = (first.getDay() + 6) % 7;
    const trailDays = (7 - ((last.getDay() + 6) % 7) - 1) % 7;
    const start = new Date(year, month - 1, 1 - leadDays);
    const end = new Date(year, month, trailDays);
    const weeks = [];
    let cursor = start;
    while (cursor <= end) {
        const week = [];
        for (let i = 0; i < 7; i += 1) {
            week.push(dayString(cursor));
            cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
        }
        weeks.push(week);
    }
    return weeks;
}

export function paddedRange(year, month) {
    const grid = monthGrid(year, month);
    return {
        from: addDays(grid[0][0], -7),
        to: addDays(grid[grid.length - 1][6], 7),
    };
}

function localDay(iso) {
    if (!iso) {
        return null;
    }
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : dayString(date);
}

export function dailyLoad(races = []) {
    const load = new Map();
    races.forEach(race => {
        if (!race || !race.reservedFrom || !race.reservedTo) {
            return;
        }
        const raceStart = localDay(race.startDate) || race.reservedFrom;
        let raceEnd = localDay(race.endDate) || raceStart;
        if (raceEnd < raceStart) {
            raceEnd = raceStart;
        }
        const needed = Number(race.trackersNeeded) || 0;
        const signups = Number(race.signups) || 0;
        const estimatedTrackers = Number(race.estimatedTrackers) || 0;
        let day = race.reservedFrom;
        while (day <= race.reservedTo) {
            const phase = day >= raceStart && day <= raceEnd ? 'race' : 'shipping';
            const entry = load.get(day) || { needed: 0, races: [] };
            load.set(day, {
                needed: entry.needed + needed,
                races: [...entry.races, {
                    id: race.id, title: race.title, trackersNeeded: needed, signups, estimatedTrackers, phase,
                }],
            });
            day = addDays(day, 1);
        }
    });
    return load;
}

export function loadLevel(needed, pool) {
    const n = Number(needed) || 0;
    const p = Number(pool) || 0;
    if (n === 0) {
        return 'free';
    }
    if (p <= 0 || n > p) {
        return 'over';
    }
    if (n >= p * 0.9) {
        return 'tight';
    }
    return 'free';
}

// Ten contrasting colours that read on both light and dark cell backgrounds.
// A race keeps its colour across months because it is picked by race id.
export const RACE_COLORS = [
    '#0d6efd', '#dc3545', '#198754', '#fd7e14', '#6f42c1',
    '#0dcaf0', '#d63384', '#795548', '#ffc107', '#20c997',
];

export function raceColor(id) {
    const n = Math.abs(parseInt(id, 10));
    const index = Number.isNaN(n) ? 0 : n % RACE_COLORS.length;
    return RACE_COLORS[index];
}

// Inline style for a race badge: race days filled with the race colour,
// shipping days outlined in it. Inline so it holds in light and dark mode.
export function raceBadgeStyle(race) {
    const color = raceColor(race && race.id);
    if (race && race.phase === 'race') {
        return `background-color: ${color}; border: 1px solid ${color}; color: #fff;`;
    }
    return `background-color: transparent; border: 1px solid ${color}; color: ${color};`;
}
